import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2015 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbWebServices } from "abweb/service/AbWebServices";
import { LocaleManager } from "abweb/service/locale/LocaleManager";
import { AbstractUserSetting } from "abweb/service/settings/UserSetting";
import { UserSettingsService } from "abweb/service/settings/UserSettingsService";
import { LogUtils } from "abweb/utils/log/LogUtils";

/**
 * Primary purpose is to lookup resources according to language, with ability to
 * prepend either hits or misses with special prefixes.  Formerly based on requireJS i1n support,
 * now based on SimpleMessageSourcePath
 * <p>
 * Additional features:
 * 1. Will log as error any resource lookup miss
 * 2. Optionally can append 'miss suffix' on any misses so that misses are apparent in UI.
 * 3. Optionally can append 'hit suffix' on any hits so that hits are apparent in UI (and thereby know that
 *    ui strings that do NOT have hit suffix must be hardcoded)
 * <p>
 * Missing features:
 * 1. string parameterization support
 * 2. additional message context needed for translation team
 */
export class I18NService {
  // Default to XX suffix on miss, and no hit suffix
  constructor(msgSource, missDelimiter = "!") {
    _defineProperty(this, "debugModeSetting", new TranslationDebugModeSetting());
    this.msgSource = msgSource;
    this.missDelimiter = missDelimiter;
  }
  registerUserSettings() {
    UserSettingsService.userSettings.registry.register(() => this.debugModeSetting);
  }

  /**
   * Invokes lookupInBundle with the (user-specific) current locale and application bundle.
   * Will optionally support missDelimiter and hitDelimiter for i18n diagnostic support.
   */
  getMessage(resourceBundleKey, msgKey, ...msgArgs) {
    let lookup = this.msgSource.getMessage(resourceBundleKey, msgKey, ...msgArgs);
    if (lookup == null) {
      const miss = this.missDelimiter ? this.missDelimiter + resourceBundleKey + ":" + msgKey + this.missDelimiter : resourceBundleKey + ":" + msgKey;
      I18NService.LOG.error(`I18NService: A resource for [resourceBundleKey = '${resourceBundleKey}', msgKey = '${msgKey}']` + ` was not found, returning value '${miss}'.`);
      return miss;
    }
    if (this.debugModeSetting.showResourceKey || LocaleManager.languageLocale === I18NService.DEBUG_LOCALE) {
      lookup = resourceBundleKey + ":" + msgKey;
    }
    const hitDelimiter = this.debugModeSetting.showResourceWithDelimiters ? "~" : undefined;
    const padChar = this.debugModeSetting.showXCharsForKey ? "X" : "";
    return hitDelimiter ? hitDelimiter + lookup + hitDelimiter : padChar ? padChar.repeat(lookup.length) : lookup;
  }
  hasMessage(resourceBundleKey, msgKey) {
    return this.msgSource.getMessage(resourceBundleKey, msgKey) != null;
  }
}
_defineProperty(I18NService, "LOG", LogUtils.getLogger(__filename));
// DG Translation Support (other products don't use this mechanism)
// Uses a special locale as a 'flag' so that dg-specific server translations also show resource keys (server and custom)
// This language variant is a nearly impossible one (Tulan spoken in Qatar) that is used to put localization into debug mode
_defineProperty(I18NService, "DEBUG_LOCALE", "to-QA");
export let TranslationDebugMode = /*#__PURE__*/function (TranslationDebugMode) {
  TranslationDebugMode["SHOW_RESOURCE_KEY"] = "show-resource-key";
  TranslationDebugMode["SHOW_RESOURCE_WITH_DELIMITERS"] = "show-resource-with-delimiters";
  TranslationDebugMode["SHOW_X_CHARS_FOR_KEY"] = "show-x-chars-for-key";
  return TranslationDebugMode;
}({});

/**
 * A simple user setting for managing the translation debug mode.
 */
class TranslationDebugModeSetting extends AbstractUserSetting {
  constructor() {
    super("TranslationDebugMode");
    _defineProperty(this, "displayName", "Translation Debug Mode");
    _defineProperty(this, "description", "This specifies the debug mode to use when rendering translated strings");
  }
  get isAvailable() {
    return AbWebServices.isDevelopmentImage();
  }
  get showResourceKey() {
    return this.value === TranslationDebugMode.SHOW_RESOURCE_KEY;
  }
  get showResourceWithDelimiters() {
    return this.value === TranslationDebugMode.SHOW_RESOURCE_WITH_DELIMITERS;
  }
  get showXCharsForKey() {
    return this.value === TranslationDebugMode.SHOW_X_CHARS_FOR_KEY;
  }
}
//# sourceMappingURL=I18NService.js.map
